import { useCallback, useEffect, useState } from "react";
import { useOnScroll } from "./useOnScroll";

type ScrollReturn<
    T extends HTMLElement,
    D extends "horizontal" | "vertical",
> = {
    scrollerRef: (instance: T | null) => void;
    scrollerNode: T | null;
} & (D extends "horizontal"
    ? { hasLeftScroll: boolean; hasRightScroll: boolean }
    : { hasTopScroll: boolean; hasBottomScroll: boolean });

export function useHasMoreToScroll<T extends HTMLElement>(
    direction?: "horizontal",
): ScrollReturn<T, "horizontal">;
export function useHasMoreToScroll<T extends HTMLElement>(
    direction?: "vertical",
): ScrollReturn<T, "vertical">;
export function useHasMoreToScroll<T extends HTMLElement>(
    direction: "horizontal" | "vertical" = "horizontal",
): ScrollReturn<T, "horizontal" | "vertical"> {
    const [hasBackwardScroll, setHasBackwardScroll] = useState(false);
    const [hasForwardScroll, setHasForwardScroll] = useState(true);

    // Note: any is used as we rely on `e.target` (can't guarentee type)
    // leaving this here until we can refactor the code to use `e.currentTarget`
    const onScroll = useCallback((e: any) => {
        const backwardScroll =
            direction === "horizontal"
                ? e.target.scrollLeft
                : e.target.scrollTop;
        const scrollSize =
            direction === "horizontal"
                ? e.target.scrollWidth
                : e.target.scrollHeight;
        const clientSize =
            direction === "horizontal"
                ? e.target.clientWidth
                : e.target.clientHeight;
        const remainingScrollForward = scrollSize - backwardScroll - clientSize;

        if (backwardScroll > 0) setHasBackwardScroll(true);
        else setHasBackwardScroll(false);

        // @ts-expect-error parseInt should only be used on strings but don't want to change functionality
        if (parseInt(remainingScrollForward) > 0) setHasForwardScroll(true);
        else setHasForwardScroll(false);
    }, []);

    const { scrollerRef, scrollerNode } = useOnScroll<T>(onScroll);

    useEffect(() => {
        if (scrollerNode) {
            const listener = onScroll.bind(null, { target: scrollerNode });
            listener();
            window.addEventListener("resize", listener);

            return () => window.removeEventListener("resize", listener);
        }
    }, [scrollerNode]);

    return {
        scrollerRef,
        scrollerNode,
        ...(direction === "horizontal"
            ? {
                  hasLeftScroll: hasBackwardScroll,
                  hasRightScroll: hasForwardScroll,
              }
            : {
                  hasTopScroll: hasBackwardScroll,
                  hasBottomScroll: hasForwardScroll,
              }),
    };
}
